import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gem, Sparkles, Flower2, BookOpen, Sun, Leaf, X, RefreshCw, Check, Trophy, Loader2 } from 'lucide-react';
import { PRODUCTS, CHAKRAS, INTENTIONS } from '../mock';
import { arcadeReward, arcadeQuiz, arcadeDaily, arcadeSave, arcadeGardenGrow, arcadeMine, isLoggedIn } from '../api';
import { useToast } from '../hooks/use-toast';
import { CrystalCatch } from '../components/CrystalCatch';

const crystals = PRODUCTS.filter((p) => p.category === 'crystals').slice(0, 6);

const useReward = () => {
  const { toast } = useToast();
  return async (game) => {
    if (!isLoggedIn()) { toast({ title: 'Log in to earn points', description: 'Create a free Prestige account to earn & save.' }); return; }
    try {
      const r = await arcadeReward(game);
      if (r.awarded > 0) toast({ title: `+${r.awarded} Prestige Points ✨`, description: `Balance: ${r.points_balance}` });
      else toast({ title: 'Daily reward limit reached', description: 'Play again — points reset daily.' });
    } catch { toast({ title: 'Could not record reward' }); }
  };
};

const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
    <div className="relative card-glass rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[88vh] overflow-y-auto">
      <button onClick={onClose} className="absolute top-4 right-4 text-[#f5ecd9]/60 hover:text-[#ff4da6]"><X className="h-6 w-6" /></button>
      {children}
    </div>
  </div>
);

// 1. CRYSTAL MATCH
const CrystalMatch = ({ onClose }) => {
  const reward = useReward();
  const deck = useMemo(() => {
    const pairs = [...crystals, ...crystals].map((c, i) => ({ key: i, c }));
    return pairs.sort(() => Math.random() - 0.5);
  }, []);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [info, setInfo] = useState(null);
  const [start] = useState(Date.now());
  const [done, setDone] = useState(false);

  const flip = (i) => {
    if (flipped.length === 2 || flipped.includes(i) || matched.includes(deck[i].c.id)) return;
    const nf = [...flipped, i];
    setFlipped(nf);
    if (nf.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = nf;
      if (deck[a].c.id === deck[b].c.id) {
        const nm = [...matched, deck[a].c.id];
        setMatched(nm); setInfo(deck[a].c);
        setFlipped([]);
        if (nm.length === crystals.length) { setDone(true); reward('crystal-match'); }
      } else setTimeout(() => setFlipped([]), 800);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="font-cinzel text-2xl text-gold-gradient text-center">Crystal Match</h2>
      <p className="text-center font-jost text-xs text-[#f5ecd9]/50 mt-1 mb-4">Moves: {moves} · Matched: {matched.length}/{crystals.length}</p>
      {done ? (
        <div className="text-center py-8">
          <Trophy className="h-12 w-12 text-[#f0d582] mx-auto mb-4" />
          <p className="font-cinzel text-xl text-gold-gradient">Complete!</p>
          <p className="font-jost text-sm text-[#f5ecd9]/60 mt-2">{moves} moves · {Math.round((Date.now() - start) / 1000)}s</p>
          <button onClick={onClose} className="btn-gold mt-6 rounded-full px-8 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Done</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2 md:gap-3">
            {deck.map((card, i) => {
              const show = flipped.includes(i) || matched.includes(card.c.id);
              return (
                <button key={card.key} onClick={() => flip(i)} className={`aspect-square rounded-xl overflow-hidden border transition-all ${show ? 'border-[#ff4da6]' : 'border-[#d4af37]/30 bg-[#120d10]'}`}>
                  {show ? <img src={card.c.image} alt="" className="w-full h-full object-cover" /> : <Gem className="h-6 w-6 text-[#d4af37]/50 mx-auto" />}
                </button>
              );
            })}
          </div>
          {info && (
            <div className="mt-4 card-glass rounded-xl p-4 text-center">
              <p className="font-cinzel text-[#f0d582]">{info.name}</p>
              <p className="font-jost text-xs text-[#f5ecd9]/60 mt-1">{info.summary}</p>
              <Link to={`/product/${info.id}`} className="text-xs text-[#ff4da6] underline mt-2 inline-block">Shop this crystal</Link>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

// 2. CHAKRA BALANCE
const ChakraBalance = ({ onClose }) => {
  const reward = useReward();
  const [i, setI] = useState(0);
  const [scores, setScores] = useState({});
  const [done, setDone] = useState(false);
  const q = CHAKRAS[i];
  const answer = (v) => {
    const ns = { ...scores, [q.name]: v };
    setScores(ns);
    if (i < CHAKRAS.length - 1) setI(i + 1);
    else { setDone(true); reward('chakra-balance'); if (isLoggedIn()) arcadeSave('chakra', 'Chakra Reflection', ns).catch(() => {}); }
  };
  const focusAreas = Object.entries(scores).filter(([, v]) => v <= 2).map(([k]) => k);
  return (
    <Modal onClose={onClose}>
      <h2 className="font-cinzel text-2xl text-gold-gradient text-center mb-1">Chakra Balance</h2>
      <p className="text-center font-jost text-[10px] text-[#f5ecd9]/40 mb-4">A spiritual self-reflection — not medical advice.</p>
      {done ? (
        <div className="py-4">
          <p className="font-cormorant text-xl text-[#f5ecd9]/80 text-center mb-4">Your reflection</p>
          <div className="flex justify-center gap-2 mb-4 flex-wrap">{CHAKRAS.map((c) => (<span key={c.name} className="h-6 w-6 rounded-full" style={{ background: c.color, opacity: (scores[c.name] || 3) / 5, boxShadow: `0 0 12px ${c.color}` }} title={c.name} />))}</div>
          {focusAreas.length ? <p className="font-jost text-sm text-[#f5ecd9]/70 text-center">Areas to explore: <span className="text-[#f0d582]">{focusAreas.join(', ')}</span>. Consider {CHAKRAS.find((c) => c.name === focusAreas[0]).crystal} and grounding rituals.</p> : <p className="font-jost text-sm text-[#f5ecd9]/70 text-center">Beautifully balanced! Keep nurturing your practice.</p>}
          <div className="text-center mt-5 flex gap-3 justify-center"><Link to="/shop?category=crystals" className="text-xs text-[#ff4da6] underline">Explore crystals</Link><Link to="/book" className="text-xs text-[#ff4da6] underline">Book a session</Link></div>
          <button onClick={onClose} className="btn-gold w-full mt-6 rounded-full py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Done</button>
        </div>
      ) : (
        <div>
          <div className="text-center mb-4"><span className="h-10 w-10 rounded-full inline-block" style={{ background: q.color, boxShadow: `0 0 20px ${q.color}` }} /><p className="font-cinzel text-lg text-[#f5ecd9] mt-2">{q.name} · {q.sanskrit}</p></div>
          <p className="font-cormorant text-xl text-[#f5ecd9]/80 text-center mb-4">How connected do you feel to your {q.focus.split(',')[0].toLowerCase()}?</p>
          <div className="flex justify-center gap-2">{[1, 2, 3, 4, 5].map((v) => (<button key={v} onClick={() => answer(v)} className="h-12 w-12 rounded-full border border-[#d4af37]/40 text-[#f0d582] hover:bg-[#d4af37]/10 font-cinzel">{v}</button>))}</div>
          <p className="text-center font-jost text-[10px] text-[#f5ecd9]/40 mt-3">{i + 1} / {CHAKRAS.length}</p>
        </div>
      )}
    </Modal>
  );
};

// 3. BUILD YOUR ALTAR
const BuildAltar = ({ onClose }) => {
  const reward = useReward();
  const { toast } = useToast();
  const [intention, setIntention] = useState(null);
  const [picks, setPicks] = useState([]);
  const items = INTENTIONS.map((x) => x);
  const elements = [...PRODUCTS.filter((p) => ['crystals', 'herbs', 'body-oils'].includes(p.category))].slice(0, 9);
  const toggle = (p) => setPicks((prev) => prev.find((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p]);
  const finish = () => { reward('build-altar'); if (isLoggedIn()) { arcadeSave('altar', intention.label, { intention: intention.label, items: picks.map((p) => ({ id: p.id, name: p.name })) }).then(() => toast({ title: 'Altar saved to your account ✨' })).catch(() => {}); } onClose(); };
  return (
    <Modal onClose={onClose}>
      <h2 className="font-cinzel text-2xl text-gold-gradient text-center mb-4">Build Your Altar</h2>
      {!intention ? (
        <><p className="font-cormorant text-xl text-[#f5ecd9]/70 text-center mb-4">Choose your intention</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{items.map((it) => (<button key={it.id} onClick={() => setIntention(it)} className="card-glass rounded-xl p-4 hover-lift"><Sparkles className="h-5 w-5 text-[#f0d582] mx-auto mb-2" /><span className="font-cinzel text-sm text-[#f5ecd9]">{it.label}</span></button>))}</div></>
      ) : (
        <><p className="font-cormorant text-lg text-[#f5ecd9]/70 text-center mb-4">Intention: <span className="text-[#f0d582]">{intention.label}</span> · pick your elements</p>
        <div className="grid grid-cols-3 gap-2">{elements.map((p) => { const on = picks.find((x) => x.id === p.id); return (<button key={p.id} onClick={() => toggle(p)} className={`rounded-xl overflow-hidden border ${on ? 'border-[#ff4da6]' : 'border-[#d4af37]/25'}`}><div className="aspect-square bg-[#120d10]"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div><span className="block text-[10px] font-jost text-[#f5ecd9]/70 p-1 truncate">{p.name}</span></button>); })}</div>
        {picks.length > 0 && <p className="font-jost text-xs text-[#f5ecd9]/60 mt-4 text-center">Your altar for <span className="text-[#f0d582]">{intention.label}</span> holds {picks.length} sacred element{picks.length > 1 ? 's' : ''}. Each carries symbolism to amplify your intention.</p>}
        <button onClick={finish} disabled={!picks.length} className="btn-gold w-full mt-5 rounded-full py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582] disabled:opacity-40">Complete Altar</button></>
      )}
    </Modal>
  );
};

// 4. HERB & CRYSTAL QUIZ
const Quiz = ({ onClose }) => {
  const reward = useReward();
  const [qs, setQs] = useState([]);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  useEffect(() => { arcadeQuiz().then((r) => setQs([...r.questions].sort(() => Math.random() - 0.5).slice(0, 6))).catch(() => {}); }, []);
  if (!qs.length) return <Modal onClose={onClose}><div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#f0d582] mx-auto" /></div></Modal>;
  const q = qs[i];
  const choose = (idx) => {
    if (sel !== null) return;
    setSel(idx);
    if (idx === q.answer) setScore((s) => s + 1);
    setTimeout(() => { if (i < qs.length - 1) { setI(i + 1); setSel(null); } else { setDone(true); reward('herb-crystal-quiz'); } }, 1400);
  };
  return (
    <Modal onClose={onClose}>
      <h2 className="font-cinzel text-2xl text-gold-gradient text-center mb-1">Herb & Crystal Quiz</h2>
      {done ? (
        <div className="text-center py-6"><Trophy className="h-12 w-12 text-[#f0d582] mx-auto mb-3" /><p className="font-cinzel text-xl text-gold-gradient">{score} / {qs.length}</p><button onClick={onClose} className="btn-gold mt-6 rounded-full px-8 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Done</button></div>
      ) : (
        <>
          <p className="text-center font-jost text-[10px] text-[#f5ecd9]/40 mb-3">Q{i + 1}/{qs.length} · Score {score}</p>
          <p className="font-cormorant text-xl text-[#f5ecd9]/85 text-center mb-5">{q.q}</p>
          <div className="space-y-2">{q.options.map((o, idx) => { let cls = 'border-[#d4af37]/25 hover:border-[#d4af37]/50'; if (sel !== null) { if (idx === q.answer) cls = 'border-[#2ecc71] bg-[#2ecc71]/10'; else if (idx === sel) cls = 'border-[#ff4da6] bg-[#ff4da6]/10'; } return (<button key={idx} onClick={() => choose(idx)} className={`w-full text-left px-4 py-3 rounded-xl border font-jost text-sm text-[#f5ecd9] ${cls}`}>{o}</button>); })}</div>
          {sel !== null && <p className="font-jost text-xs text-[#f5ecd9]/60 mt-3 text-center">{q.explain}</p>}
        </>
      )}
    </Modal>
  );
};

// 5. DAILY INTENTION
const DailyPull = ({ onClose }) => {
  const reward = useReward();
  const [pull, setPull] = useState(null);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => { if (isLoggedIn()) arcadeDaily().then((r) => setPull(r)).catch(() => {}); }, []);
  const reveal = () => { setRevealed(true); if (pull && !pull.already_claimed) { reward('daily-intention'); arcadeSave('daily', pull.pull.intention, pull.pull).catch(() => {}); } };
  if (!isLoggedIn()) return <Modal onClose={onClose}><div className="text-center py-8"><Sun className="h-10 w-10 text-[#f0d582] mx-auto mb-3" /><p className="font-cormorant text-xl text-[#f5ecd9]/70">Log in to receive your daily energy pull.</p><Link to="/account" className="btn-gold inline-block mt-5 rounded-full px-8 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Log In</Link></div></Modal>;
  return (
    <Modal onClose={onClose}>
      <h2 className="font-cinzel text-2xl text-gold-gradient text-center mb-4">Daily Intention</h2>
      {!revealed ? (
        <div className="text-center py-8"><button onClick={reveal} className="h-40 w-32 mx-auto rounded-2xl border border-[#d4af37]/40 bg-gradient-to-br from-[#1a0f16] to-[#0a0708] flex items-center justify-center floaty hover:scale-105 transition-transform"><Sparkles className="h-10 w-10 text-[#f0d582]" /></button><p className="font-jost text-sm text-[#f5ecd9]/50 mt-4">Tap the card to reveal today's energy</p></div>
      ) : pull && (
        <div className="card-glass rounded-2xl p-6 text-center">
          <p className="font-jost text-[10px] uppercase tracking-widest text-[#ff4da6]">{pull.date}</p>
          <h3 className="font-cinzel text-2xl text-gold-gradient mt-2">{pull.pull.intention}</h3>
          <p className="font-cormorant text-xl text-[#f5ecd9]/80 italic mt-2">“{pull.pull.affirmation}”</p>
          <div className="grid grid-cols-3 gap-2 mt-4 text-xs font-jost text-[#f5ecd9]/60"><div>💎 {pull.pull.crystal}</div><div>🌿 {pull.pull.herb}</div><div>✨ {pull.pull.chakra}</div></div>
          <p className="font-jost text-sm text-[#f5ecd9]/70 mt-4">{pull.pull.meditation}</p>
          <p className="font-cormorant text-lg text-[#f0d582] mt-2">{pull.pull.prompt}</p>
          {pull.already_claimed && <p className="font-jost text-[10px] text-[#f5ecd9]/40 mt-3">Already claimed today — revisit anytime.</p>}
          <button onClick={onClose} className="btn-gold mt-5 rounded-full px-8 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Done</button>
        </div>
      )}
    </Modal>
  );
};

// 6. MEDITATION GARDEN
const Garden = ({ onClose }) => {
  const reward = useReward();
  const { toast } = useToast();
  const [garden, setGarden] = useState(null);
  const stages = ['🌱', '🌿', '☘️', '🌸', '🪷'];
  useEffect(() => { if (isLoggedIn()) arcadeMine().then((m) => setGarden(m.garden)).catch(() => {}); }, []);
  const activities = ['Guided breathing', 'Short meditation', 'Gratitude entry', 'Reflection prompt', 'Wellness check-in'];
  const grow = async (a) => {
    try { const r = await arcadeGardenGrow(a); setGarden(r.garden); if (r.awarded > 0) toast({ title: `+${r.awarded} points · ${a}` }); else toast({ title: `${a} complete 🌱`, description: 'Daily point limit reached — garden still grows!' }); } catch { toast({ title: 'Could not save' }); }
  };
  if (!isLoggedIn()) return <Modal onClose={onClose}><div className="text-center py-8"><Flower2 className="h-10 w-10 text-[#f0d582] mx-auto mb-3" /><p className="font-cormorant text-xl text-[#f5ecd9]/70">Log in to grow your Meditation Garden.</p><Link to="/account" className="btn-gold inline-block mt-5 rounded-full px-8 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]">Log In</Link></div></Modal>;
  return (
    <Modal onClose={onClose}>
      <h2 className="font-cinzel text-2xl text-gold-gradient text-center mb-2">Meditation Garden</h2>
      <div className="text-center py-6">
        <div className="text-7xl mb-3">{stages[garden ? garden.stage : 0]}</div>
        <p className="font-cinzel text-lg text-[#f0d582]">{garden ? garden.stage_name : 'Seed'}</p>
        <p className="font-jost text-xs text-[#f5ecd9]/50 mt-1">{garden ? garden.progress : 0} wellness activities completed</p>
      </div>
      <p className="font-cormorant text-lg text-[#f5ecd9]/70 text-center mb-3">Complete an activity to nurture your garden</p>
      <div className="grid grid-cols-2 gap-2">{activities.map((a) => (<button key={a} onClick={() => grow(a)} className="card-glass rounded-xl px-4 py-3 font-jost text-sm text-[#f5ecd9] hover:border-[#ff4da6]/50 border border-transparent">{a}</button>))}</div>
    </Modal>
  );
};

const GAMES = [
  { id: 'crystal-catch', title: 'Crystal Catch', desc: 'Fast-paced: catch falling crystals, beat your best', icon: Gem, C: ({ onClose }) => (<Modal onClose={onClose}><CrystalCatch /></Modal>) },
  { id: 'crystal-match', title: 'Crystal Match', desc: 'Match crystals & learn their meaning', icon: Gem, C: CrystalMatch },
  { id: 'chakra-balance', title: 'Chakra Balance', desc: 'A seven-chakra self-reflection', icon: Sparkles, C: ChakraBalance },
  { id: 'build-altar', title: 'Build Your Altar', desc: 'Craft a sacred altar for your intention', icon: Flower2, C: BuildAltar },
  { id: 'herb-crystal-quiz', title: 'Herb & Crystal Quiz', desc: 'Test your holistic wisdom', icon: BookOpen, C: Quiz },
  { id: 'daily-intention', title: 'Daily Intention', desc: 'Your once-daily energy pull', icon: Sun, C: DailyPull },
  { id: 'meditation-garden', title: 'Meditation Garden', desc: 'Grow a garden through mindfulness', icon: Leaf, C: Garden },
];

const Arcade = () => {
  const [active, setActive] = useState(null);
  const ActiveGame = active ? GAMES.find((g) => g.id === active).C : null;
  return (
    <div className="pt-28 pb-24 min-h-screen">
      <section className="relative py-12 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-[#d6197d]/15 blur-[120px] glow-pulse" />
        <div className="relative max-w-3xl mx-auto px-5 text-center">
          <Sparkles className="h-12 w-12 text-[#ff4da6] mx-auto mb-4 floaty" />
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">Play · Learn · Earn</p>
          <h1 className="font-cinzel text-4xl md:text-6xl text-gold-gradient">Wellness Arcade</h1>
          <div className="gold-divider my-6" />
          <p className="font-cormorant text-xl md:text-2xl text-[#f5ecd9]/75">Six mindful mini-games to explore crystals, chakras and intention — earn Prestige Points daily.</p>
          {!isLoggedIn() && <p className="font-jost text-xs text-[#f0d582]/80 mt-4"><Link to="/account" className="underline">Log in</Link> to earn & save your progress.</p>}
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {GAMES.map((g) => (
            <button key={g.id} onClick={() => setActive(g.id)} className="card-glass rounded-2xl p-8 text-center hover-lift group">
              <div className="h-16 w-16 mx-auto rounded-full border border-[#d4af37]/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><g.icon className="h-7 w-7 text-[#f0d582]" /></div>
              <h3 className="font-cinzel text-xl text-[#f5ecd9]">{g.title}</h3>
              <p className="font-jost text-sm text-[#f5ecd9]/55 mt-2">{g.desc}</p>
              <span className="inline-block mt-4 text-xs uppercase tracking-widest text-[#ff4da6] font-jost">Play →</span>
            </button>
          ))}
        </div>
        <p className="text-center font-jost text-xs text-[#f5ecd9]/40 mt-10">Points are validated & saved server-side. Daily limits apply — mindful play, not point farming. 💫</p>
      </section>
      {ActiveGame && <ActiveGame onClose={() => setActive(null)} />}
    </div>
  );
};

export default Arcade;
